import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import type { ConfirmDialogProps } from './types';

const getConfig = (type: ConfirmDialogProps['type']) => {
  switch (type) {
    case 'danger':
      return {
        icon: XCircleIcon,
        iconBg: 'bg-red-500/10',
        iconColor: 'text-red-500',
        buttonColor: 'bg-red-500 hover:bg-red-600',
      };
    case 'success':
      return {
        icon: CheckCircleIcon,
        iconBg: 'bg-success/10',
        iconColor: 'text-success',
        buttonColor: 'bg-success hover:bg-[#16A34A]',
      };
    case 'info':
      return {
        icon: InformationCircleIcon,
        iconBg: 'bg-info/10',
        iconColor: 'text-info',
        buttonColor: 'bg-primary hover:bg-primary-dark',
      };
    default:
      return {
        icon: ExclamationTriangleIcon,
        iconBg: 'bg-warning/10',
        iconColor: 'text-warning',
        buttonColor: 'bg-primary hover:bg-primary-dark',
      };
  }
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  type = 'warning',
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  overlayClassName = 'fixed inset-0 bg-black/60 backdrop-blur-sm',
  panelClassName = 'w-full max-w-md transform overflow-hidden rounded-2xl bg-bg-elevated p-6 shadow-2xl transition-all border border-border-subtle',
  cancelButtonClassName = 'px-5 py-2.5 text-sm font-medium text-gray-300 bg-bg-elevated border border-border-subtle rounded-lg hover:bg-bg-surface hover:border-border-default transition-all duration-200 cursor-pointer',
  confirmButtonClassName,
}) => {
  const { icon: IconComponent, iconBg, iconColor, buttonColor } = getConfig(type);

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onCancel} className="relative z-[9999]">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={overlayClassName} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className={panelClassName}>
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 w-10 h-10 ${iconBg} rounded-xl flex items-center justify-center`}>
                    <IconComponent className={`h-5 w-5 ${iconColor}`} />
                  </div>
                  <div className="flex-1 pt-1">
                    <Dialog.Title className="text-lg font-semibold text-gray-50">{title}</Dialog.Title>
                    <Dialog.Description className="mt-2 text-sm text-gray-400 leading-relaxed">
                      {message}
                    </Dialog.Description>
                  </div>
                </div>

                <div className="mt-6 flex gap-3 justify-end">
                  <button type="button" onClick={onCancel} className={cancelButtonClassName}>
                    {cancelText}
                  </button>
                  <button
                    type="button"
                    onClick={onConfirm}
                    className={confirmButtonClassName ?? `px-5 py-2.5 text-sm font-medium text-white rounded-lg ${buttonColor} transition-all duration-200 shadow-lg`}
                  >
                    {confirmText}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ConfirmDialog;
